import React, { useRef, useState } from 'react';
import { Camera, Upload, CheckCircle, RefreshCw, Trash2, Copy, Download } from 'lucide-react';
import CustomModal from './CustomModal';
import PlayerAvatar from './PlayerAvatar';
import CropImageModal from './CropImageModal';

const PhotoUploadModal = ({
  isOpen,
  onClose,
  player,
  uploading = false,
  uploadSuccess = null,
  onFileSelect,
  onCameraCapture,
  onRemove,
  entityLabel = 'jugador',
  entityName,
  imageUrl
}) => {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [pendingHandler, setPendingHandler] = useState(null);
  const resolvedName = entityName || player?.nombre_completo || '';
  const resolvedImageUrl = imageUrl || player?.imagen_url || null;
  const downloadImage = (url) => {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = resolvedName ? `${resolvedName}.jpg` : 'foto.jpg';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };
  const copyUrl = (url) => {
    if (!url) return;
    navigator.clipboard?.writeText(url);
  };

  const openCropModal = (file, handler) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result);
      setPendingHandler(() => handler);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && onFileSelect) {
      openCropModal(file, onFileSelect);
    }
    event.target.value = '';
  };

  const handleCameraCapture = (event) => {
    const file = event.target.files[0];
    if (file && onCameraCapture) {
      openCropModal(file, onCameraCapture);
    }
    event.target.value = '';
  };

  return (
    <>
      <CustomModal 
        isOpen={isOpen} 
        onClose={onClose}
        title={`Cambiar foto de ${entityLabel}`}
        size="md"
      >
        <div className="text-center">
          {uploadSuccess ? (
            <div className="py-6">
              <div className="mb-4">
                <PlayerAvatar
                  imageUrl={uploadSuccess.url || resolvedImageUrl}
                  name={resolvedName}
                  size="large"
                  className="mx-auto mb-3"
                />
                {(uploadSuccess.url || resolvedImageUrl) && (
                  <div className="flex items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => copyUrl(uploadSuccess.url || resolvedImageUrl)}
                      className="text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded"
                      title="Copiar URL"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => downloadImage(uploadSuccess.url || resolvedImageUrl)}
                      className="text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded"
                      title="Descargar"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                )}
              </div>
              <CheckCircle className="mx-auto text-green-500 mb-3" size={40} />
              <h3 className="text-lg font-semibold text-foreground mb-2">Foto actualizada</h3>
              <p className="text-muted-foreground text-sm">
                La foto de {uploadSuccess.player?.nombre_completo || resolvedName} se ha guardado correctamente.
              </p>
            </div>
          ) : uploading ? (
            <div className="py-8">
              <RefreshCw className="mx-auto text-blue-500 mb-4 animate-spin" size={48} />
              <h3 className="text-lg font-semibold text-foreground mb-2">Subiendo foto...</h3>
              <p className="text-muted-foreground text-sm">
                Por favor espera mientras se procesa la imagen.
              </p>
            </div>
          ) : (
            <div>
              <div className="mb-6">
                <PlayerAvatar
                  imageUrl={resolvedImageUrl}
                  name={resolvedName}
                  size="large"
                  className="mx-auto mb-4"
                />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {resolvedName}
                </h3>
                <p className="text-muted-foreground text-sm">
                  Selecciona una foto para este {entityLabel}
                </p>
                {resolvedImageUrl && (
                  <div className="mt-3 flex items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => copyUrl(resolvedImageUrl)}
                      className="text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded"
                      title="Copiar URL"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => downloadImage(resolvedImageUrl)}
                      className="text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded"
                      title="Descargar"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="w-full bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center space-x-2"
                >
                  <Camera size={20} />
                  <span>Tomar foto</span>
                </button>

                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full bg-foreground/70 text-background px-6 py-3 rounded-lg font-semibold hover:bg-foreground/80 transition-colors flex items-center justify-center space-x-2"
                >
                  <Upload size={20} />
                  <span>Subir desde galeria</span>
                </button>

                {resolvedImageUrl && (
                  <button
                    onClick={() => onRemove?.(player)}
                    className="w-full bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Trash2 size={20} />
                    <span>Eliminar foto</span>
                  </button>
                )}

                <button
                  onClick={onClose}
                  className="w-full bg-background text-muted-foreground px-6 py-3 rounded-lg font-semibold border border-border hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      </CustomModal>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCameraCapture}
        className="hidden"
      />

      <CropImageModal
        isOpen={cropOpen}
        imageSrc={cropImageSrc}
        onCancel={() => {
          setCropOpen(false);
          setCropImageSrc(null);
          setPendingHandler(null);
        }}
        onConfirm={(croppedFile) => {
          if (pendingHandler) {
            pendingHandler(croppedFile);
          }
          setCropOpen(false);
          setCropImageSrc(null);
          setPendingHandler(null);
        }}
      />
    </>
  );
};

export default PhotoUploadModal;
